import { useTheme } from "@emotion/react";
import { faHome } from "@fortawesome/pro-solid-svg-icons";
import {
    type Color,
    type ColorSet,
    type ColorValue,
    type Provider,
} from "@towni/common";
import { ButtonTransparent } from "../buttons_v2/button-gray";
import { Icon } from "../icons/icon";
import { useNavigateTo } from "../text/navigate-to";

/**
 * A button component that navigates to a provider's home page when clicked.
 * The button displays a house icon with customizable colors based on the theme and provided color props.
 *
 * @param props - The component props
 * @param props.provider - The provider object containing the slug for navigation
 * @param props.color - Optional color value that can be a ColorValue, Color, or ColorSet.
 *                     Defaults to theme's default background color if not provided.
 *
 * @example
 * ```tsx
 * <ProviderHomeButton
 *   provider={{ slug: 'provider-1' }}
 *   color={theme.colors.primary}
 * />
 * ```
 */
const ProviderHomeButton = (props: {
    readonly provider: Provider;
    readonly color?: ColorValue | Color | ColorSet;
    readonly disableShadow?: boolean;
}) => {
    const theme = useTheme();
    const { provider } = props;
    const navigate = useNavigateTo();
    const color = props.color ?? theme.colors.default.background;

    // If we want to use a duotone icon we can
    // use the following same logic as in the AccountIcon component
    // to determine the color of the icon based on the background color
    // >>>
    // const isColorLight = isHexColor(color)
    //     ? isLightColor(color)
    //     : isColorValue(color)
    //       ? color.isLight
    //       : isColorSet(color)
    //         ? color.main.value.isLight
    //         : color.value.isLight;

    // const color2 = isColorLight
    //     ? theme.colors.black.light40.asString
    //     : theme.colors.white.asString;

    return (
        <ButtonTransparent
            css={{ padding: 0 }}
            onClick={() => {
                navigate(`/home/${provider.slug}`);
            }}>
            <Icon
                icon={faHome}
                size={1.3}
                color={color}
                // colors={{
                //     primaryColor: color2,
                //     secondaryColor: color,
                //     primaryOpacity: 1,
                //     secondaryOpacity: 1,
                // }}
                css={
                    props.disableShadow
                        ? undefined
                        : {
                              willChange: "filter",
                              filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.1))",
                              path: {
                                  stroke: "rgba(0,0,0,0.2)",
                                  strokeWidth: "2rem",
                              },
                          }
                }
            />
        </ButtonTransparent>
    );
};

export { ProviderHomeButton };
